import {useAnalytics} from '@github-ui/use-analytics'
import {type Icon, TriangleDownIcon} from '@primer/octicons-react'
import {Button, NavList} from '@primer/react'
import {Dialog} from '@primer/react/experimental'
import {useRef, useState} from 'react'

import styles from './ListSidebar.module.css'

export interface SidebarLink {
  id: string
  query: string
  title: string
  IconComponent: Icon
}

interface ListSidebarProps {
  links: SidebarLink[]
  onQueryChanged: (query: string) => void
  selectedLink: string
}

export function ListSidebar({links, selectedLink, onQueryChanged}: ListSidebarProps) {
  const {sendAnalyticsEvent} = useAnalytics()

  return (
    <NavList>
      {links.map(({id, query, title, IconComponent}) => (
        <NavList.Item
          aria-current={selectedLink === id && 'page'}
          key={id}
          href={`?q=${query}`}
          onClick={(event: React.MouseEvent) => {
            sendAnalyticsEvent('repos-list.sidebar', 'SIDEBAR_PICKER', {id})
            if (canSoftNavigate(event)) {
              onQueryChanged(query)
              event.preventDefault()
            }
          }}
        >
          <NavList.LeadingVisual>
            <IconComponent />
          </NavList.LeadingVisual>
          {title}
        </NavList.Item>
      ))}
    </NavList>
  )
}

export function ListSidebarCollapsedButton({onQueryChanged, ...props}: ListSidebarProps) {
  const [isDialogOpen, showDialog] = useState(false)
  const buttonRef = useRef<HTMLButtonElement>(null)
  const selectedLinkItem = props.links.find(link => link.id === props.selectedLink)
  const listTitle = selectedLinkItem?.title || 'Search'

  return (
    <>
      <Button
        ref={buttonRef}
        variant="invisible"
        onClick={() => showDialog(true)}
        trailingVisual={TriangleDownIcon}
        aria-describedby="types-collapsed-button-description"
        className={styles.sidebarToggleButton}
      >
        <h1 className={styles.sidebarHeading}>{listTitle}</h1>
      </Button>
      <span className="sr-only" id="types-collapsed-button-description" aria-live="polite">
        Change the repository type filter
      </span>
      {isDialogOpen && (
        <Dialog onClose={() => showDialog(false)} title="Types" width="small" returnFocusRef={buttonRef}>
          <ListSidebar
            onQueryChanged={q => {
              onQueryChanged(q)
              showDialog(false)
            }}
            {...props}
          />
        </Dialog>
      )}
    </>
  )
}

function canSoftNavigate(event: React.MouseEvent) {
  // eslint-disable-next-line @github-ui/ui-commands/no-manual-shortcut-logic -- Handle locally unless user is opening on a new tab
  return !event.ctrlKey && !event.metaKey && !event.shiftKey && event.button === 0
}

try{ ListSidebar.displayName ||= 'ListSidebar' } catch {}
try{ ListSidebarCollapsedButton.displayName ||= 'ListSidebarCollapsedButton' } catch {}